import { Box, Stack } from '@mui/material';
import { compact } from 'lodash';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import {
  PrimaryCellTypography,
  SecondaryCellTypography,
} from '@/components/tables/DataTable/components/cells';
import { StyledTableCell } from '@/components/tables/DisplayTable/DisplayTable';
import { StyledTableRow } from '@/components/tables/DisplayTable/StyledTableRow';
import { SharedIntegrationEntityTypeahead } from '@/modules/assetProviderIntegrations/shared/SharedIntegrationEntityTypeahead';

import { EntityLinkFormShape, EntityLinkRow } from './EntityLinkTable.types';

export interface EntityLinkTableRowProps {
  row: EntityLinkRow;
  householdId: string;
  /**
   * Every integration entity selected anywhere in this pane. Used to stop the
   * same integration entity from being linked to two Luminary entities.
   */
  selectedIntegrationEntityIds: Set<string>;
}

export function EntityLinkTableRow({
  row,
  householdId,
  selectedIntegrationEntityIds,
}: EntityLinkTableRowProps) {
  const { control } = useFormContext<EntityLinkFormShape>();

  const currentIntegrationEntities = useWatch({
    control,
    name: `entities.${row.id}.integrationEntities`,
  });

  const getOptionDisabled = useCallback(
    (optionValue: string) =>
      selectedIntegrationEntityIds.has(optionValue) &&
      !(currentIntegrationEntities || []).includes(optionValue),
    [selectedIntegrationEntityIds, currentIntegrationEntities]
  );

  // "$28,650,000 | Logan Roy | Revocable trust", dropping any segment the
  // entity doesn't have (businesses carry no grantor or donor, for instance).
  const supportingText = compact([
    row.formattedValue,
    row.ownerNames.length ? row.ownerNames.join(' & ') : null,
    row.typeLabel,
  ]).join(' | ');

  return (
    <StyledTableRow>
      <StyledTableCell width="50%">
        <Stack>
          <PrimaryCellTypography>{row.displayName}</PrimaryCellTypography>
          <SecondaryCellTypography>{supportingText}</SecondaryCellTypography>
        </Stack>
      </StyledTableCell>

      <StyledTableCell width="50%">
        <Box>
          <SharedIntegrationEntityTypeahead<EntityLinkFormShape>
            control={control}
            fieldName={`entities.${row.id}.integrationEntities`}
            includeAllEntitiesFieldName={`entities.${row.id}.includeAllEntities`}
            filterToHouseholdId={householdId}
            inputOnly
            getOptionDisabled={getOptionDisabled}
          />
        </Box>
      </StyledTableCell>
    </StyledTableRow>
  );
}
